/**
 * Data Import Wizard — assignability lattice data module (S6.T1, relocated S6.T2a).
 *
 * Split out of `assignability.ts` to keep it under the ≤200-line rule. Pure
 * lookup tables + small capacity-comparison helpers `canAssign` dispatches to.
 *
 * Process-agnostic shared module (src/features/workspace/data-import/mapping/):
 * no React/zustand/DOM/electron-runtime imports — both the renderer and the
 * main process import this SAME code (S6.T2a design ruling, "one gate, no
 * second list"). `CanonicalType`/`CanonicalTypeKind` are type-only imports,
 * erased at build — routed through `src/types/electron.d.ts` (NOT a direct
 * `.ts` import into `electron/cross-engine/`) because a fully-checked `src/`
 * file hitting `electron/*.ts` directly trips the tsconfig.node.json
 * composite project-reference boundary (TS6305); the ambient `.d.ts` is the
 * codebase's existing sanctioned crossing point (see its own header comment).
 */
import type { CanonicalType, CanonicalTypeKind } from '../../../../types/electron';

// Mirrors `electron/cross-engine/canonicalType.ts`'s `CANONICAL_LENGTH_MAX`
// (sentinel for unbounded length) — kept local so this shared module never
// runtime-imports electron/.
const CANONICAL_LENGTH_MAX = -1;

export const CHAR_KINDS: ReadonlySet<CanonicalTypeKind> = new Set(['char', 'varchar', 'text']);
export const BINARY_KINDS: ReadonlySet<CanonicalTypeKind> = new Set(['binary', 'varbinary', 'blob']);
export const INT_KINDS: ReadonlySet<CanonicalTypeKind> = new Set(['int8', 'int16', 'int32', 'int64']);
export const FLOAT_KINDS: ReadonlySet<CanonicalTypeKind> = new Set(['float32', 'float64']);
/** Kinds `canAssign` knows how to safely render as a character string (the
 *  "X → text/varchar" stringify promotion). `unknown` is deliberately excluded
 *  — we don't know its shape, so it's never assumed stringifiable. */
export const STRINGIFIABLE_SCALAR_KINDS: ReadonlySet<CanonicalTypeKind> = new Set([
  'boolean',
  'int8',
  'int16',
  'int32',
  'int64',
  'decimal',
  'float32',
  'float64',
  'date',
  'time',
  'timestamp',
  'timestamptz',
  'json',
]);

type IntKind = 'int8' | 'int16' | 'int32' | 'int64';
type FloatKind = 'float32' | 'float64';

/** Ladder rank — higher = wider. Same rank = identity = ok. */
const INT_RANK: Record<IntKind, number> = { int8: 1, int16: 2, int32: 3, int64: 4 };
const FLOAT_RANK: Record<FloatKind, number> = { float32: 1, float64: 2 };
/** Magnitude bits (excludes sign bit) — used for the int→float capacity check. */
const INT_MAGNITUDE_BITS: Record<IntKind, number> = { int8: 7, int16: 15, int32: 31, int64: 63 };
/** Exact-integer mantissa bits. */
const FLOAT_MANTISSA_BITS: Record<FloatKind, number> = { float32: 24, float64: 53 };
/** Minimum characters a uuid needs to round-trip as text (`xxxxxxxx-xxxx-...`). */
const UUID_MIN_LENGTH = 36;

export function intLadderVerdict(source: IntKind, target: IntKind): 'ok' | 'lossy' {
  return INT_RANK[target] >= INT_RANK[source] ? 'ok' : 'lossy';
}

export function floatLadderVerdict(source: FloatKind, target: FloatKind): 'ok' | 'lossy' {
  return FLOAT_RANK[target] >= FLOAT_RANK[source] ? 'ok' : 'lossy';
}

/** int → float: ok only when the integer's full magnitude fits exactly in the
 *  float's mantissa (capacity comparison, not a hand-listed pair table). */
export function intToFloatVerdict(source: IntKind, target: FloatKind): 'ok' | 'lossy' {
  return INT_MAGNITUDE_BITS[source] <= FLOAT_MANTISSA_BITS[target] ? 'ok' : 'lossy';
}

/** decimal(p,s) → decimal(p,s): ok iff target can hold at least as many
 *  integer digits AND at least as many fractional digits as the source. */
export function decimalVerdict(source: CanonicalType, target: CanonicalType): 'ok' | 'lossy' {
  const sourceScale = source.scale ?? 0;
  const targetScale = target.scale ?? 0;
  const sourceIntDigits = (source.precision ?? 0) - sourceScale;
  const targetIntDigits = (target.precision ?? 0) - targetScale;
  return targetIntDigits >= sourceIntDigits && targetScale >= sourceScale ? 'ok' : 'lossy';
}

/** A length-bearing type with no length set, or `text`/`blob` (inherently
 *  unbounded regardless of any length facet), never truncates. */
export function isUnboundedLength(c: CanonicalType): boolean {
  if (c.kind === 'text' || c.kind === 'blob') return true;
  return c.length === undefined || c.length === CANONICAL_LENGTH_MAX;
}

function effectiveLength(c: CanonicalType): number {
  return c.length ?? CANONICAL_LENGTH_MAX;
}

/** char-family ↔ char-family or binary-family ↔ binary-family: unbounded
 *  target always fits; unbounded source into a bounded target can't be
 *  guaranteed to fit; otherwise compare lengths directly. Never 'invalid' —
 *  callers only reach this once both sides are confirmed same-family. */
export function lengthVerdict(source: CanonicalType, target: CanonicalType): 'ok' | 'lossy' {
  if (isUnboundedLength(target)) return 'ok';
  if (isUnboundedLength(source)) return 'lossy';
  return effectiveLength(target) >= effectiveLength(source) ? 'ok' : 'lossy';
}

/** uuid → bounded char-family target: needs ≥36 chars to round-trip; an
 *  unbounded target always fits. */
export function uuidToCharVerdict(target: CanonicalType): 'ok' | 'lossy' {
  if (isUnboundedLength(target)) return 'ok';
  return effectiveLength(target) >= UUID_MIN_LENGTH ? 'ok' : 'lossy';
}

/** Fractional-seconds width for a temporal value: the `.` separator + the
 *  recorded sub-second `precision` (0 when the inference lattice saw none). */
function fractionWidth(c: CanonicalType): number {
  const p = c.precision ?? 0;
  return p > 0 ? p + 1 : 0;
}

/**
 * Worst-case character width of a stringifiable scalar rendered as text — used
 * for the "scalar → bounded varchar(n)/char(n)" length check. `undefined` =
 * effectively unbounded (`json`: a document has no type-level width ceiling, so
 * like `text` it never fits a bounded target). Widths are UPPER bounds (sign +
 * full magnitude / full ISO form), so `target.length >= width` guarantees a
 * fit and a shorter target is `lossy` — the safe direction, since `canAssign`
 * is the SOLE bounded-length safety net (`prevalidate.ts` leaves overflow to
 * the driver). Temporal sub-second width comes from `precision` when inference
 * recorded one, else second-precision is assumed.
 */
export function stringifiedMaxWidth(source: CanonicalType): number | undefined {
  switch (source.kind) {
    case 'boolean': return 5; // 'false'
    case 'int8': return 4; // '-128'
    case 'int16': return 6; // '-32768'
    case 'int32': return 11; // '-2147483648'
    case 'int64': return 20; // '-9223372036854775808'
    // float32 is totality-only (inference never emits it as a source); float64
    // needs a PROVABLE ceiling: JS `String()` of a double peaks at 25 chars for
    // the fixed-notation boundary case (`-0.0000010000000000000002`), longer
    // than the 24-char exponential form — so 26 keeps `width` an upper bound.
    case 'float32': return 26;
    case 'float64': return 26;
    case 'decimal': return (source.precision ?? 38) + 2; // digits + sign + '.'
    case 'date': return 10; // 'YYYY-MM-DD'
    case 'time': return 8 + fractionWidth(source); // 'HH:MM:SS'
    case 'timestamp': return 19 + fractionWidth(source); // 'YYYY-MM-DD HH:MM:SS'
    case 'timestamptz': return 19 + fractionWidth(source) + 6; // + '+HH:MM'
    default: return undefined; // json (+ any non-width-modeled kind) → unbounded
  }
}

/** Stringifiable scalar → char-family target: an unbounded target always fits;
 *  an unbounded-width source (`json`) never fits a bounded target; otherwise
 *  compare the source's worst-case width to the target's length. */
export function stringifiedScalarToCharVerdict(source: CanonicalType, target: CanonicalType): 'ok' | 'lossy' {
  if (isUnboundedLength(target)) return 'ok';
  const width = stringifiedMaxWidth(source);
  if (width === undefined) return 'lossy';
  return effectiveLength(target) >= width ? 'ok' : 'lossy';
}
